import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CRMNote } from '../../types/notes';
import type { MeetingReport } from '../../types/meetingReport';
import type { NoteGroup } from './groupNotes';
import { NoteListItem } from './NoteListItem';

interface NoteGroupListProps {
  groups: NoteGroup[];
  selectedNoteId: string | null;
  onSelect: (id: string) => void;
  findReport: (noteId: string, appointmentId?: string | null) => MeetingReport | null;
}

/** Rendu des notes en sections repliables, une par valeur de l'axe de groupement. */
export function NoteGroupList({ groups, selectedNoteId, onSelect, findReport }: NoteGroupListProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-2 p-2">
      {groups.map((group) => {
        const isCollapsed = collapsed.has(group.key);
        return (
          <div key={group.key}>
            <button
              onClick={() => toggle(group.key)}
              className="w-full flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              {isCollapsed ? (
                <ChevronRight className="w-3 h-3 shrink-0" />
              ) : (
                <ChevronDown className="w-3 h-3 shrink-0" />
              )}
              <span className="truncate">{group.label}</span>
              <span className="ml-auto text-neutral-400 tabular-nums">{group.notes.length}</span>
            </button>
            {!isCollapsed &&
              group.notes.map((note: CRMNote) => (
                <NoteListItem
                  key={note.id}
                  note={note}
                  isSelected={selectedNoteId === note.id}
                  onClick={() => onSelect(note.id)}
                  preloadedReport={findReport(note.id, note.appointment_id)}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}
